import { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Modal,
  Typography,
  Image,
  Upload,
  Form,
  App,
  Radio,
  Checkbox,
  Tag,
} from "antd";
import {
  CloseOutlined,
  UploadOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import ChatIcon from "../../assets/icons/chat-icon.svg";
import CustomInput from "../../components/CustomInput/Input";
import CustomDatePicker from "../../components/CustomDatePicker/CustomDatePicker";
import FormFooter from "../../components/CustomForms/FormFooter";
import { useSelector, useDispatch } from "react-redux";
import {
  submitBusQustions,
  getBusQustions,
  verifyBusPan,
  verifyBusGstin,
  verifyIfsc,
  verifyBank,
  getConsentDigilocker,
  getKycDetails,
  clearQuestionnaireErrors,
} from "../../redux/slices/QuestionnaireSlice";
import { BTN_TXT, SUCCESS_MSGS } from "../../constants/constants";
import {
  handleUploadFile,
  ifscRules,
  panRules,
  gstinRules,
  accNumberRules,
  nameRules,
  address1Rules,
  address2Rules,
  cityRules,
  stateRules,
  pincodeRules,
} from "../../helpers/helpers";
import { getQuestionnaireTextRules, getJvQuestionDisplay, formatQuestionnaireAnswerForDisplay, sanitizeQuestionnaireAnswerForSubmit, getQuestionnaireInputProps, isReferenceLinksQuestion } from "../../helpers/jvQuestionnaireAnswers";
import {
  OPTIONAL_QUESTION_SKIP_ANSWER,
  isQuestionnaireQuestionOptional,
  isSkippedOptionalAnswer,
} from "../../helpers/questionnaireOptional";
import {
  shouldShowManualKycFields,
  getManualKycDocumentType,
  isManualKycFileUploadRequired,
  buildManualKycAnswerText,
  parseManualKycAnswerText,
  isHypervergeManualEligible,
  isManualEntryAnswer,
  MANUAL_KYC_BANK_FIELD_NAMES,
  MANUAL_KYC_DIRECTOR_AADHAAR_FIELD_NAMES,
  MANUAL_KYC_COMPANY_ADDRESS_FIELD_NAMES,
  MANUAL_KYC_GSTIN_FIELD_NAMES,
} from "../../helpers/manualKycQuestionnaire";
import {
  EMPTY_REFERENCE_LINK,
  formatReferenceLinks,
  parseReferenceLinks,
} from "../../helpers/referenceLinks";
import ReferenceLinksFormList from "../../components/ReferenceLinksFormList/ReferenceLinksFormList";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomButton from "../../components/CustomButton/Button";

const { Text } = Typography;

const QuestionCardModal = ({ open, onClose, selectedQuestion }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const displayQuestion = getJvQuestionDisplay(selectedQuestion || {});
  const questionTitle = displayQuestion.questionTitle;
  const questionnaireInputProps = getQuestionnaireInputProps(questionTitle);
  const isReferenceLinksQ = isReferenceLinksQuestion(questionTitle);

  const buildAnswerText = (value) => {
    const sanitized = sanitizeQuestionnaireAnswerForSubmit(questionTitle, value);
    return Array.isArray(sanitized) ? sanitized.join(", ") : sanitized;
  };

  const buildReferenceLinksAnswerText = (otherLinks) => {
    const formatted = formatReferenceLinks(otherLinks);
    return formatted?.length ? JSON.stringify(formatted) : undefined;
  };
  const {
    error,
    submitLoading,
    submitError,
    panError,
    gstinError,
    panLoading,
    gstinLoading,
    ifscData,
    ifscLoading,
    bankLoading,
    digilockerLoading,
    kycDetails,
  } = useSelector((state) => state.questionnaire);
  const { user } = useSelector((state) => state.auth);
  const [fileUrl, setFileUrl] = useState(null);
  const [panFileUrl, setPanFileUrl] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isIfscVerified, setIsIfscVerified] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);
  const { activeCategory } = useSelector((state) => state.dealCategory);
  const isBusPan = selectedQuestion?.questionId === "QBM1000002";
  const isCompGstin = selectedQuestion?.questionId === "QBM1000015";
  const isBank = selectedQuestion?.questionId === "QBM1000014";
  const isDirPanAadhaar = selectedQuestion?.questionId === "QBM1000043";
  const isOptionalQuestion = isQuestionnaireQuestionOptional(selectedQuestion);
  const isHvManualEligible = isHypervergeManualEligible(
    selectedQuestion?.questionId,
  );
  const isManualKycDoc = shouldShowManualKycFields(
    selectedQuestion?.questionId,
    selectedQuestion?.threadName,
    isManualMode,
  );
  const manualKycType = isManualKycDoc
    ? getManualKycDocumentType(selectedQuestion?.questionId)
    : null;
  const manualKycRequiresFileUpload = isManualKycFileUploadRequired(
    selectedQuestion?.questionId,
    selectedQuestion?.threadName,
    isManualMode,
  );
  const isManualKycAddressOnly =
    isManualKycDoc && manualKycType === "COMPANY_ADDRESS";
  const isDirectorAadhaarManual =
    isManualKycDoc && manualKycType === "DIRECTOR_AADHAAR";
  // Document questions always get upload; Switch-to-Manual also requires supporting docs
  const isFileUploadQuestion =
    (selectedQuestion?.questionTypeId === "QTM1000008" &&
      !isManualKycAddressOnly) ||
    (isManualMode && isHvManualEligible);
  const isDefaultQuestion =
    isBusPan || isCompGstin || isBank || isDirPanAadhaar;
  const response = Form.useWatch("response", form);
  const ifsc = Form.useWatch("ifsc", form);
  const isBankVerified = kycDetails?.kycBank?.isVerified;
  const isAadhaarPanVerified =
    kycDetails?.kycDirectorsAadhaarValue?.isVerified &&
    kycDetails?.kycDirectorsPanValue?.isVerified;

  useEffect(() => {
    if (open) {
      dispatch(clearQuestionnaireErrors());
      setIsIfscVerified(false);
      setIsManualMode(false);
      const draft = selectedQuestion?.answers?.[0];
      if (draft?.answerText && isManualEntryAnswer(draft.answerText)) {
        setIsManualMode(true);
      }
    }
  }, [open, selectedQuestion?.threadQuestionId, dispatch, selectedQuestion?.answers]);

  useEffect(() => {
    // Hyperverge bank only — manual mode stores IFSC in answerText without API verify
    if (!isBank || isManualKycDoc || isManualMode) return;

    const handleVerifyIfsc = async () => {
      if (
        ifsc &&
        /^[A-Z]{4}[0][A-Z0-9]{6}$/.test(ifsc) &&
        !isIfscVerified
      ) {
        const payload = {
          ifscCode: ifsc,
          orgId: user?.orgId,
          dealCategoryId: activeCategory?.dealCategoryId,
        };
        const res = await dispatch(verifyIfsc(payload));
        if (res.payload.statusCode === 201) {
          setIsIfscVerified(true);
        }
      }
    };
    handleVerifyIfsc();
  }, [
    ifsc,
    dispatch,
    user?.orgId,
    isIfscVerified,
    isBank,
    isManualKycDoc,
    isManualMode,
    activeCategory?.dealCategoryId,
  ]);

  useEffect(() => {
    if (isDefaultQuestion) {
      dispatch(getKycDetails());
    }
  }, [isDefaultQuestion, dispatch, activeCategory?.dealCategoryId]);

  useEffect(() => {
    if (!open || !selectedQuestion) return;

    const draftAnswer = selectedQuestion.answers?.[0];
    if (draftAnswer) {
      const isManualDraft = isManualEntryAnswer(draftAnswer.answerText);

      if (isManualDraft && draftAnswer.answerText) {
        const parsed = parseManualKycAnswerText(draftAnswer.answerText);
        if (parsed) {
          form.setFieldsValue({
            pan: parsed.pan,
            gstin: parsed.gstin,
            ifsc: parsed.ifsc,
            accNum: parsed.accNum,
            bankName: parsed.bankName,
            branchName: parsed.branchName,
            accHolderName: parsed.accHolderName,
            directorName: parsed.directorName,
            aadhaar: parsed.aadhaar,
            addressLine1: parsed.addressLine1,
            addressLine2: parsed.addressLine2,
            city: parsed.city,
            state: parsed.state,
            pincode: parsed.pinCode,
          });
        }
        if (draftAnswer.fileUrl) {
          setFileUrl(draftAnswer.fileUrl);
          form.setFieldsValue({
            manualDoc: [
              {
                uid: "-1",
                name: draftAnswer.fileUrl.split("/").pop(),
                status: "done",
                url: draftAnswer.fileUrl,
              },
            ],
          });
        }
        if (parsed?.panFileUrl) {
          setPanFileUrl(parsed.panFileUrl);
          form.setFieldsValue({
            panDoc: [
              {
                uid: "-2",
                name: parsed.panFileUrl.split("/").pop(),
                status: "done",
                url: parsed.panFileUrl,
              },
            ],
          });
        }
        return;
      }

      if (selectedQuestion.questionTypeId === "QTM1000004") {
        const dateVal = draftAnswer.answerText ? dayjs(draftAnswer.answerText) : null;
        form.setFieldsValue({ response: dateVal });
      } else if (selectedQuestion.questionTypeId === "QTM1000007") {
        const checkboxVal = draftAnswer.answerText
          ? draftAnswer.answerText.split(",").map((s) => s.trim())
          : [];
        form.setFieldsValue({ response: checkboxVal });
      } else if (selectedQuestion.questionTypeId === "QTM1000008") {
        if (draftAnswer.fileUrl) {
          setFileUrl(draftAnswer.fileUrl);
          form.setFieldsValue({
            response: [
              {
                uid: "-1",
                name: draftAnswer.fileUrl.split("/").pop(),
                status: "done",
                url: draftAnswer.fileUrl,
              },
            ],
          });
        }
      } else if (isReferenceLinksQuestion(displayQuestion.questionTitle)) {
        if (isSkippedOptionalAnswer(draftAnswer.answerText)) {
          form.setFieldsValue({ otherLinks: [{ ...EMPTY_REFERENCE_LINK }] });
        } else {
          form.setFieldsValue({
            otherLinks: parseReferenceLinks(draftAnswer.answerText),
          });
        }
      } else {
        form.setFieldsValue({
          response: formatQuestionnaireAnswerForDisplay(
            displayQuestion.questionTitle,
            draftAnswer.answerText,
          ),
        });
      }
      return;
    }

    form.resetFields();
    if (isReferenceLinksQuestion(displayQuestion.questionTitle)) {
      form.setFieldsValue({ otherLinks: [{ ...EMPTY_REFERENCE_LINK }] });
    }
    setFileUrl(null);
    setPanFileUrl(null);
    setIsVerified(false);
    setIsManualMode(false);
  }, [open, selectedQuestion, form, displayQuestion.questionTitle]);

  const handleSaveAnswer = async () => {
    const values = form.getFieldsValue();
    const referenceLinksAnswer = isReferenceLinksQ
      ? buildReferenceLinksAnswerText(values.otherLinks)
      : undefined;
    const hasResponse = isFileUploadQuestion
      ? !!fileUrl
      : isReferenceLinksQ
        ? !!referenceLinksAnswer
      : values.response !== undefined && values.response !== null && values.response !== "";

    if (!hasResponse && !isManualKycDoc) {
      message.warning(
        isFileUploadQuestion
          ? "Please upload a document successfully before saving."
          : isReferenceLinksQ
            ? "Please add a reference link before saving, or use Skip."
          : "Please enter an answer before saving.",
      );
      return;
    }

    if (isReferenceLinksQ) {
      try {
        await form.validateFields();
      } catch {
        return;
      }
    }

    if (isManualKycDoc) {
      try {
        await form.validateFields(
          manualKycType === "BANK"
            ? MANUAL_KYC_BANK_FIELD_NAMES
            : manualKycType === "DIRECTOR_AADHAAR"
              ? MANUAL_KYC_DIRECTOR_AADHAAR_FIELD_NAMES
              : manualKycType === "COMPANY_ADDRESS"
                ? MANUAL_KYC_COMPANY_ADDRESS_FIELD_NAMES
            : manualKycType === "PAN" || manualKycType === "DIRECTOR_PAN"
              ? ["pan"]
              : manualKycType === "GSTIN"
                ? MANUAL_KYC_GSTIN_FIELD_NAMES
                : ["aadhaar"],
        );
      } catch {
        return;
      }
    }

    if (isDirectorAadhaarManual && (!fileUrl || !panFileUrl)) {
      message.warning(
        "Please upload both Aadhaar and PAN documents before saving.",
      );
      return;
    }

    const payload = {
      threadQuestionId: selectedQuestion.threadQuestionId,
      dealCategoryId: activeCategory?.dealCategoryId,
      questionId: selectedQuestion.questionId,
      orgId: user.orgId,
      isDraft: true,
      ...(isManualMode || isManualKycDoc ? { isManualMode: true } : {}),
    };

    if (selectedQuestion.questionTypeId === "QTM1000004") {
      payload.answerText = values.response ? values.response.format("YYYY-MM-DD") : undefined;
    } else if (isManualKycAddressOnly) {
      payload.answerText = buildManualKycAnswerText(manualKycType, values);
    } else if (isManualKycDoc || isManualMode) {
      if (isFileUploadQuestion) {
        payload.fileUrl = fileUrl;
      }
      payload.answerText = buildManualKycAnswerText(manualKycType, {
        ...values,
        ...(isDirectorAadhaarManual ? { panFileUrl } : {}),
      });
    } else if (selectedQuestion.questionTypeId === "QTM1000008") {
      if (isFileUploadQuestion) {
        payload.fileUrl = fileUrl;
      }
    } else if (isReferenceLinksQ) {
      payload.answerText = referenceLinksAnswer;
    } else {
      payload.answerText = buildAnswerText(values.response);
    }

    setSaveLoading(true);
    try {
      const res = await dispatch(submitBusQustions(payload));
      if (res.payload.statusCode === 201) {
        message.success("Draft saved successfully.");
        onClose();
        form.resetFields();
        setFileUrl(null);
        setPanFileUrl(null);
        setIsVerified(false);
        dispatch(
          getBusQustions({
            orgId: user.orgId,
            status: "PENDING",
            dealCategoryId: activeCategory?.dealCategoryId,
          }),
        );
      } else {
        message.error(res?.payload?.message || "Failed to save draft.");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to save draft.");
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !kycDetails) return;

    if (isBank && kycDetails?.kycBank?.isVerified) {
      form.setFieldsValue({
        response: kycDetails.kycBank.docValue,
      });

      setIsVerified(true);
    }

    if (isCompGstin && kycDetails?.kycGstinValue?.isVerified) {
      form.setFieldsValue({
        response: kycDetails.kycGstinValue.docValue,
      });

      setIsVerified(true);
    }

    if (isBusPan && kycDetails?.kycPanValue?.isVerified) {
      form.setFieldsValue({
        response: kycDetails.kycPanValue.docValue,
      });

      setIsVerified(true);
    }

    if (isDirPanAadhaar && isAadhaarPanVerified) {
      form.setFieldsValue({
        response: `${kycDetails?.kycDirectorsPanValue?.docValue}, ${kycDetails?.kycDirectorsAadhaarValue?.docValue}`,
      });

      setIsVerified(true);
    }
  }, [
    open,
    kycDetails,
    isBank,
    isCompGstin,
    isBusPan,
    form,
    isDirPanAadhaar,
    isAadhaarPanVerified,
  ]);

  const handleSubmit = async (values) => {
    if (isDirectorAadhaarManual) {
      if (!fileUrl || !panFileUrl) {
        message.warning(
          "Please upload both Aadhaar and PAN documents before submitting.",
        );
        return;
      }
    } else if ((isFileUploadQuestion || manualKycRequiresFileUpload) && !fileUrl) {
      message.warning("Please upload a document successfully before submitting.");
      return;
    }

    if (isReferenceLinksQ) {
      const referenceLinksAnswer = buildReferenceLinksAnswerText(values.otherLinks);
      if (!referenceLinksAnswer) {
        message.warning(
          isOptionalQuestion
            ? "Please add a reference link, or use Skip if you have none."
            : "Please add at least one reference link with a description.",
        );
        return;
      }
    }

    if (isManualKycDoc || isManualMode) {
      try {
        await form.validateFields(
          manualKycType === "BANK"
            ? MANUAL_KYC_BANK_FIELD_NAMES
            : manualKycType === "DIRECTOR_AADHAAR"
              ? MANUAL_KYC_DIRECTOR_AADHAAR_FIELD_NAMES
              : manualKycType === "COMPANY_ADDRESS"
                ? MANUAL_KYC_COMPANY_ADDRESS_FIELD_NAMES
            : manualKycType === "PAN" || manualKycType === "DIRECTOR_PAN"
              ? ["pan"]
              : manualKycType === "GSTIN"
                ? MANUAL_KYC_GSTIN_FIELD_NAMES
                : ["aadhaar"],
        );
      } catch {
        return;
      }
    }

    const payload = {
      threadQuestionId: selectedQuestion.threadQuestionId,
      dealCategoryId: activeCategory?.dealCategoryId,
      questionId: selectedQuestion.questionId,
      orgId: user.orgId,
      ...(isManualMode || isManualKycDoc ? { isManualMode: true } : {}),
    };
    if (selectedQuestion.questionTypeId === "QTM1000004") {
      payload.answerText = values.response.format("YYYY-MM-DD");
    } else if (isManualKycAddressOnly) {
      payload.answerText = buildManualKycAnswerText(manualKycType, values);
    } else if (isManualKycDoc || isManualMode) {
      if (isFileUploadQuestion || manualKycRequiresFileUpload) {
        payload.fileUrl = fileUrl;
      }
      payload.answerText = buildManualKycAnswerText(manualKycType, {
        ...values,
        ...(isDirectorAadhaarManual ? { panFileUrl } : {}),
      });
    } else if (selectedQuestion.questionTypeId === "QTM1000008") {
      if (isFileUploadQuestion) {
        payload.fileUrl = fileUrl;
      }
    } else if (isReferenceLinksQ) {
      payload.answerText = buildReferenceLinksAnswerText(values.otherLinks);
    } else {
      payload.answerText = buildAnswerText(values.response);
    }
    const res = await dispatch(submitBusQustions(payload));
    if (res.payload.statusCode === 201) {
      message.success(
        isManualMode
          ? "Submitted for Admin review. Manual KYC values require Admin approval."
          : SUCCESS_MSGS.QUESTIONNAIRE_SUBMITTED_SUCCESS,
      );
      onClose();
      form.resetFields();
      setFileUrl(null);
      setPanFileUrl(null);
      setIsVerified(false);
      setIsManualMode(false);
      dispatch(
        getBusQustions({
          orgId: user.orgId,
          status: "PENDING",
          dealCategoryId: activeCategory?.dealCategoryId,
        }),
      );
    }
  };

  const refreshPendingQuestions = () => {
    dispatch(
      getBusQustions({
        orgId: user.orgId,
        status: "PENDING",
        dealCategoryId: activeCategory?.dealCategoryId,
      }),
    );
  };

  const handleSkipAnswer = async () => {
    if (!isOptionalQuestion) return;

    const payload = {
      threadQuestionId: selectedQuestion.threadQuestionId,
      dealCategoryId: activeCategory?.dealCategoryId,
      questionId: selectedQuestion.questionId,
      orgId: user.orgId,
      answerText: OPTIONAL_QUESTION_SKIP_ANSWER,
    };

    setSkipLoading(true);
    try {
      const res = await dispatch(submitBusQustions(payload));
      if (res.payload.statusCode === 201) {
        message.success("Question skipped. Answer recorded as N/A.");
        onClose();
        form.resetFields();
        setFileUrl(null);
        setPanFileUrl(null);
        setIsVerified(false);
        refreshPendingQuestions();
      } else {
        message.error(res?.payload?.message || "Failed to skip question.");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to skip question.");
    } finally {
      setSkipLoading(false);
    }
  };

  const handleBeforeUpload = (file) => {
    const allowedTypes =
      isManualMode || isDirectorAadhaarManual
        ? ["pdf", "jpg", "jpeg", "png"]
        : selectedQuestion?.answerOptions || ["pdf", "jpg", "jpeg", "png", "docx"];
    const fileType = file.name.split(".").pop().toLowerCase();
    const isAllowed = allowedTypes.includes(fileType);

    if (!isAllowed) {
      message.error(`Only ${allowedTypes.join(", ")} files are allowed`);
      return Upload.LIST_IGNORE;
    }

    const isLt2MB = file.size / 1024 / 1024 < 20;
    if (!isLt2MB) {
      message.error("File must be smaller than 20MB!");
      return Upload.LIST_IGNORE;
    }

    return true; // Allow upload
  };

  const handleCustomRequest = async ({ file, onSuccess, onError }, setUrl) => {
    const cleanName = file.name.replace(/\s+/g, "_");
    const cleanFile = new File([file], cleanName, { type: file.type });

    try {
      const path = `business/${user.orgId}`;
      const { fileUrl: uploadedUrl } = await handleUploadFile(cleanFile, path);
      setUrl(uploadedUrl);
      message.success("File uploaded successfully");
      onSuccess("ok");
    } catch (err) {
      console.error("Upload error:", err);
      message.error(
        "Document upload failed. Please try again or contact support if the issue continues.",
      );
      onError(err);
    }
  };

  const buildUploadProps = (setUrl, acceptOverride) => ({
    name: "file",
    multiple: false,
    accept:
      acceptOverride ||
      selectedQuestion?.answerOptions?.map((opt) => `.${opt}`).join(", ") ||
      ".pdf, .jpg, .jpeg, .png, .docx",
    maxCount: 1,
    beforeUpload: handleBeforeUpload,
    customRequest: (options) => handleCustomRequest(options, setUrl),
  });

  const uploadProps = buildUploadProps(setFileUrl);
  const panUploadProps = buildUploadProps(setPanFileUrl, ".pdf, .jpg, .jpeg, .png");
  const aadhaarUploadProps = buildUploadProps(
    setFileUrl,
    ".pdf, .jpg, .jpeg, .png",
  );

  const handleVerifyBtnClick = async () => {
    let payload = {};
    let action;
    if (isBusPan) {
      payload = {
        pan: response,
        orgId: user.orgId,
        busName: user?.orgName,
        dealCategoryId: activeCategory?.dealCategoryId,
      };
      action = verifyBusPan;
    } else if (isCompGstin) {
      payload = {
        gstin: response,
        orgId: user.orgId,
        dealCategoryId: activeCategory?.dealCategoryId,
      };
      action = verifyBusGstin;
    } else if (isBank) {
      payload = {
        accHolderName: user?.orgName,
        accNum: response,
        ifscId: ifscData.ifscId,
        orgId: user.orgId,
        dealCategoryId: activeCategory?.dealCategoryId,
      };
      action = verifyBank;
    }

    const res = await dispatch(action(payload));
    if (res?.payload?.statusCode === 201) {
      setIsVerified(true);
    }
  };

  const getValidation = () => {
    if (isBusPan) {
      return !/[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(response || "");
    } else if (isCompGstin) {
      return !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        response || "",
      );
    } else if (isBank) {
      return !(response && response.length >= 8 && response.length <= 16);
    }
  };

  const handleProceedDigi = async () => {
    const res = await dispatch(
      getConsentDigilocker({
        orgId: user?.orgId,
        dealCategoryId: activeCategory?.dealCategoryId,
      }),
    );

    if (res?.payload.response) {
      window.location.href = res.payload.response;
    }
  };

  const onCancelModal = () => {
    dispatch(clearQuestionnaireErrors());
    onClose();
    form.resetFields();
    setFileUrl(null);
    setPanFileUrl(null);
    setIsVerified(false);
    setIsManualMode(false);
  };

  return (
    <Modal
      open={open}
      footer={null}
      closeIcon={null}
      centered
      width={620}
      className="question-bank-modal"
      destroyOnHidden
    >
      {selectedQuestion && (
        <div className="question-bank-modal-content">
          {/* Top Section */}
          <div className="question-bank-modal-top">
            <span className="question-bank-task-status">To-Do</span>

            <button
              className="question-bank-modal-close"
              type="button"
              onClick={onCancelModal}
            >
              <CloseOutlined />
            </button>
          </div>

          <Text className="question-bank-thread-text">
            Thread-based communication for this question
          </Text>

          {/* Admin Question Card */}
          <div className="question-bank-admin-question-card">
            <div className="question-bank-admin-header">
              <Image src={ChatIcon} preview={false} width={18} />
              <Text className="question-bank-admin-title">Admin Question</Text>
              {isOptionalQuestion && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  Optional
                </Tag>
              )}
              <Text className="question-bank-admin-date">
                {selectedQuestion.dueDate}
              </Text>
            </div>

            <Text className="question-bank-admin-description">
              {displayQuestion.questionTitle}
            </Text>
          </div>

          {selectedQuestion.status === "REJECTED" && selectedQuestion.answers?.[0]?.latestReviewComment && (
            <div className="question-bank-rejection-card">
              <div className="rejection-header">
                <CloseCircleOutlined style={{ color: "#c53030", fontSize: "16px" }} />
                <span className="rejection-title">Reason for Rejection</span>
              </div>
              <p className="rejection-comment">
                {selectedQuestion.answers[0].latestReviewComment}
              </p>
            </div>
          )}

          <Form layout="vertical" form={form} onFinish={handleSubmit}>
            {selectedQuestion.questionTypeId === "QTM1000003" &&
              isBank &&
              !isBankVerified &&
              !isManualMode && (
                <CustomInput
                  label="IFSC Code"
                  name="ifsc"
                  type="text"
                  isRequired={true}
                  disabled={isIfscVerified}
                  placeholder="Enter IFSC"
                  rules={ifscRules}
                  suffix={ifscLoading && <LoadingOutlined />}
                />
              )}
            {(selectedQuestion.questionTypeId === "QTM1000001" ||
            selectedQuestion.questionTypeId === "QTM1000003") &&
            !isManualKycAddressOnly &&
            !isManualMode &&
            !isReferenceLinksQ ? (
              <CustomInput
                label={isBank ? "Account Number" : "Your Response"}
                name="response"
                isRequired
                verifyBtn={isBusPan || isCompGstin || isBank}
                isVerified={isVerified}
                disabled={(isBank && !isIfscVerified) || isVerified}
                disableVerify={isVerified || getValidation()}
                verifyLoading={panLoading || gstinLoading || bankLoading}
                handleVerify={handleVerifyBtnClick}
                placeholder={
                  displayQuestion.placeholder || selectedQuestion.placeholder || "Enter your response"
                }
                rules={getQuestionnaireTextRules(displayQuestion.questionTitle)}
                normalize={questionnaireInputProps.normalize}
                getValueProps={questionnaireInputProps.getValueProps}
                inputMode={questionnaireInputProps.inputMode}
                onChange={questionnaireInputProps.onChange}
              />
            ) : null}
            {isHvManualEligible &&
              !isDirPanAadhaar &&
              !isManualMode &&
              !isVerified && (
              <div style={{ marginBottom: 16, textAlign: "left" }}>
                {(panError || gstinError) && (
                  <Text type="danger" style={{ display: "block", marginBottom: 8 }}>
                    Verification failed. You can switch to manual entry.
                  </Text>
                )}
                <CustomButton
                  type="link"
                  style={{
                    fontSize: 14,
                    padding: 0,
                    height: "auto",
                    display: "block",
                    textAlign: "left",
                  }}
                  onClick={() => {
                    setIsManualMode(true);
                    dispatch(clearQuestionnaireErrors());
                    setIsVerified(false);
                    form.setFieldsValue({
                      pan: isBusPan ? response : undefined,
                      gstin: isCompGstin ? response : undefined,
                      accNum: isBank ? response : undefined,
                      ifsc: isBank ? ifsc : undefined,
                    });
                  }}
                >
                  Having trouble? Switch to Manual
                </CustomButton>
              </div>
            )}
            {isManualMode && (
              <Tag color="orange" style={{ marginBottom: 12 }}>
                Manual entry — Admin approval required
              </Tag>
            )}
            {isReferenceLinksQ ? (
              <ReferenceLinksFormList listName="otherLinks" />
            ) : selectedQuestion.questionTypeId === "QTM1000002" ? (
              <CustomInput
                label="Your Response"
                name="response"
                isRequired={!isOptionalQuestion}
                placeholder={
                  selectedQuestion.placeholder || "Enter your response"
                }
                rules={
                  isOptionalQuestion
                    ? []
                    : [
                        {
                          required: true,
                          message: "Please enter your response",
                        },
                      ]
                }
                isTextArea
              />
            ) : null}
            {selectedQuestion.questionTypeId === "QTM1000004" ? (
              <CustomDatePicker
                label="Select Date"
                name="response"
                isRequired={true}
                placeholder="Select Date"
                rules={[{ required: true, message: "Please select a date" }]}
                disabledDate={(current) => current && current >= dayjs().startOf("day")}
              />
            ) : null}
            {selectedQuestion.questionTypeId === "QTM1000005" ? (
              <CustomSelect
                label="Select Option"
                name="response"
                isRequired={true}
                placeholder="Select an option"
                rules={[{ required: true, message: "Please select an option" }]}
                options={
                  selectedQuestion?.answerOptions?.map((opt) => ({
                    value: opt,
                    label: opt,
                  })) || []
                }
              />
            ) : null}
            {selectedQuestion.questionTypeId === "QTM1000006" ? (
              <Form.Item
                label={
                  <Text>
                    Select Option<Text type="danger"> *</Text>
                  </Text>
                }
                name="response"
                rules={[{ required: true, message: "Please select an option" }]}
                className="form-item-class"
              >
                <Radio.Group
                  options={
                    selectedQuestion?.answerOptions?.map((opt) => ({
                      label: opt,
                      value: opt,
                    })) || []
                  }
                />
              </Form.Item>
            ) : null}
            {selectedQuestion.questionTypeId === "QTM1000007" &&
            !isAadhaarPanVerified &&
            !isManualMode ? (
              <Form.Item
                label={
                  isDirPanAadhaar ? null : (
                    <Text>
                      Select Options<Text type="danger"> *</Text>
                    </Text>
                  )
                }
                name="response"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one option",
                  },
                ]}
                className="form-item-class"
              >
                <Checkbox.Group
                  options={
                    selectedQuestion?.answerOptions?.map((opt) => ({
                      label: opt,
                      value: opt,
                    })) || []
                  }
                />
              </Form.Item>
            ) : null}
            {isDirPanAadhaar && isAadhaarPanVerified && !isManualMode && (
              <CustomInput
                label="Your Response"
                name="response"
                isRequired
                disabled={isVerified}
                verifyBtn={true}
                isVerified={isVerified}
                disableVerify={isVerified}
              />
            )}
            {isManualKycDoc &&
              (manualKycType === "PAN" || manualKycType === "DIRECTOR_PAN") && (
                <CustomInput
                  label="PAN Number"
                  name="pan"
                  isRequired
                  placeholder="Enter PAN number"
                  rules={panRules}
                />
              )}
            {isManualKycDoc && manualKycType === "GSTIN" && (
              <>
                <CustomInput
                  label="GSTIN Number"
                  name="gstin"
                  isRequired
                  placeholder="Enter GSTIN number"
                  rules={gstinRules}
                />
                <CustomInput
                  label="Address 1"
                  name="addressLine1"
                  type="text"
                  isRequired
                  placeholder="Enter Address 1"
                  rules={address1Rules}
                />
                <CustomInput
                  label="Address 2"
                  name="addressLine2"
                  type="text"
                  isRequired
                  placeholder="Enter Address 2"
                  rules={address2Rules}
                />
                <CustomInput
                  label="City"
                  name="city"
                  type="text"
                  isRequired
                  placeholder="Enter City"
                  allowLettersOnly
                  rules={cityRules}
                />
                <CustomInput
                  label="Pincode"
                  name="pincode"
                  type="text"
                  isRequired
                  placeholder="Enter Pincode"
                  allowNumbersOnly
                  maxLength={6}
                  rules={pincodeRules}
                />
                <CustomInput
                  label="State"
                  name="state"
                  type="text"
                  isRequired
                  placeholder="Enter State"
                  allowLettersOnly
                  rules={stateRules}
                />
              </>
            )}
            {isManualKycDoc && manualKycType === "BANK" && (
              <>
                <CustomInput
                  label="IFSC Code"
                  name="ifsc"
                  type="text"
                  isRequired
                  placeholder="Enter IFSC"
                  rules={ifscRules}
                />
                <CustomInput
                  label="Bank Name"
                  name="bankName"
                  type="text"
                  isRequired
                  placeholder="Enter bank name"
                  rules={[
                    { required: true, message: "Bank name is required" },
                    { min: 2, message: "Please enter a valid bank name" },
                  ]}
                />
                <CustomInput
                  label="Branch Name"
                  name="branchName"
                  type="text"
                  isRequired
                  placeholder="Enter branch name"
                  rules={[
                    { required: true, message: "Branch name is required" },
                    { min: 2, message: "Please enter a valid branch name" },
                  ]}
                />
                <CustomInput
                  label="Account Holder Name"
                  name="accHolderName"
                  type="text"
                  isRequired
                  placeholder="Enter account holder name"
                  rules={nameRules("Account Holder")}
                />
                <CustomInput
                  label="Bank Account Number"
                  name="accNum"
                  isRequired
                  placeholder="Enter account number"
                  rules={accNumberRules}
                />
              </>
            )}
            {isManualKycDoc && manualKycType === "DIRECTOR_AADHAAR" && (
              <>
                <CustomInput
                  label="Director Name"
                  name="directorName"
                  type="text"
                  isRequired
                  placeholder="Enter director name"
                  rules={nameRules("Director")}
                />
                <CustomInput
                  label="Aadhaar Number"
                  name="aadhaar"
                  isRequired
                  placeholder="Enter Aadhaar number"
                  rules={[
                    { required: true, message: "Aadhaar number is required" },
                    {
                      pattern: /^\d{12}$/,
                      message: "Please enter a valid 12-digit Aadhaar",
                    },
                  ]}
                />
                <CustomInput
                  label="Director PAN Number"
                  name="pan"
                  isRequired
                  placeholder="Enter Director PAN number"
                  rules={panRules}
                />
              </>
            )}
            {isManualKycDoc && manualKycType === "COMPANY_ADDRESS" && (
              <>
                <CustomInput
                  label="Address 1"
                  name="addressLine1"
                  type="text"
                  isRequired
                  placeholder="Enter Address 1"
                  rules={address1Rules}
                />
                <CustomInput
                  label="Address 2"
                  name="addressLine2"
                  type="text"
                  isRequired
                  placeholder="Enter Address 2"
                  rules={address2Rules}
                />
                <CustomInput
                  label="City"
                  name="city"
                  type="text"
                  isRequired
                  placeholder="Enter City"
                  allowLettersOnly
                  rules={cityRules}
                />
                <CustomInput
                  label="Pincode"
                  name="pincode"
                  type="text"
                  isRequired
                  placeholder="Enter Pincode"
                  allowNumbersOnly
                  maxLength={6}
                  rules={pincodeRules}
                />
                <CustomInput
                  label="State"
                  name="state"
                  type="text"
                  isRequired
                  placeholder="Enter State"
                  allowLettersOnly
                  rules={stateRules}
                />
              </>
            )}
            {isFileUploadQuestion && isDirectorAadhaarManual ? (
              <>
                <Form.Item
                  label={
                    <Text>
                      Upload Aadhaar Document
                      <Text type="danger"> *</Text>
                    </Text>
                  }
                  name="manualDoc"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => e?.fileList}
                  rules={[
                    {
                      required: true,
                      message: "Please upload Aadhaar document",
                    },
                  ]}
                  className="form-item-class"
                >
                  <Upload.Dragger
                    {...aadhaarUploadProps}
                    className="question-bank-uploader"
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="question-bank-upload-title">
                      Click to upload Aadhaar document
                    </p>
                    <p className="question-bank-upload-subtitle">
                      Accepted file types: PDF, JPG, JPEG, PNG. Max file size: 10MB.
                    </p>
                  </Upload.Dragger>
                </Form.Item>
                <Form.Item
                  label={
                    <Text>
                      Upload PAN Document
                      <Text type="danger"> *</Text>
                    </Text>
                  }
                  name="panDoc"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => e?.fileList}
                  rules={[
                    {
                      required: true,
                      message: "Please upload PAN document",
                    },
                  ]}
                  className="form-item-class"
                >
                  <Upload.Dragger
                    {...panUploadProps}
                    className="question-bank-uploader"
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="question-bank-upload-title">
                      Click to upload PAN document
                    </p>
                    <p className="question-bank-upload-subtitle">
                      Accepted file types: PDF, JPG, JPEG, PNG. Max file size: 10MB.
                    </p>
                  </Upload.Dragger>
                </Form.Item>
              </>
            ) : isFileUploadQuestion ? (
              <Form.Item
                label={
                  <Text>
                    {isManualMode
                      ? "Upload Supporting Document"
                      : "Upload Your Response"}
                    {!isOptionalQuestion && <Text type="danger"> *</Text>}
                  </Text>
                }
                name={isManualMode ? "manualDoc" : "response"}
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
                rules={
                  isOptionalQuestion
                    ? []
                    : [
                        {
                          required: true,
                          message: "Please upload your response",
                        },
                      ]
                }
                className="form-item-class"
              >
                <Upload.Dragger
                  {...uploadProps}
                  className="question-bank-uploader"
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="question-bank-upload-title">
                    Click to upload documents
                  </p>
                  <p className="question-bank-upload-subtitle">
                    {isManualMode
                      ? "Accepted file types: PDF, JPG, JPEG, PNG. Max file size: 10MB."
                      : `Accepted file types: ${selectedQuestion?.answerOptions?.map((opt) => opt).join(", ")}. Max file size: 10MB.`}
                  </p>
                </Upload.Dragger>
              </Form.Item>
            ) : null}
            {isDirPanAadhaar && !isAadhaarPanVerified && !isManualMode && (
              <>
                <CustomButton
                  type="primary"
                  disabled={!response || response?.length === 0}
                  loading={digilockerLoading}
                  onClick={handleProceedDigi}
                >
                  {BTN_TXT.PRO_WITH_DIGI}
                </CustomButton>
                <div style={{ marginTop: 12, marginBottom: 8, textAlign: "left" }}>
                  <CustomButton
                    type="link"
                    style={{
                      fontSize: 14,
                      padding: 0,
                      height: "auto",
                      display: "block",
                      textAlign: "left",
                    }}
                    onClick={() => {
                      setIsManualMode(true);
                      dispatch(clearQuestionnaireErrors());
                      setIsVerified(false);
                    }}
                  >
                    Having trouble? Switch to Manual
                  </CustomButton>
                </div>
              </>
            )}
            {(isAadhaarPanVerified || !isDirPanAadhaar || isManualMode) && (
              <FormFooter
                onCancelClick={onCancelModal}
                submitLoading={submitLoading}
                disabled={
                  ((isBusPan || isCompGstin || isBank) &&
                    !isVerified &&
                    !isManualMode) ||
                  (isDirectorAadhaarManual
                    ? !fileUrl || !panFileUrl
                    : (isFileUploadQuestion || manualKycRequiresFileUpload) &&
                      !fileUrl)
                }
                error={
                  error ||
                  submitError ||
                  (!isManualKycDoc && !isManualMode && (panError || gstinError))
                }
                btnName="Submit Answer"
                showSaveDraft={!isDefaultQuestion || isManualMode}
                onSaveClick={handleSaveAnswer}
                saveLoading={saveLoading}
                showSkip={isOptionalQuestion && !isDefaultQuestion}
                onSkipClick={handleSkipAnswer}
                skipLoading={skipLoading}
              />
            )}
          </Form>
        </div>
      )}
    </Modal>
  );
};

export default QuestionCardModal;
